import { ImageIcon, Share2 } from "lucide-react";
import { useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PopoverErrorBoundary } from "@/components/error-boundary/popover-error-boundary";
import { SettingCard } from "@/components/settings/setting-card";
import { useIsMobile } from "@/hooks/hooks/use-mobile";
import { BackButton, BaseNavigationBar } from "@/lib/components/navigation";
import { FormActionButtons, NavigationGuardProvider } from "@/lib/components/navigation-guard";
import { PageContent } from "@/lib/components/page-content";
import { SettingsSearchTrigger } from "../../search/components/settings-search-trigger";
import { AnnouncementCard } from "./components/announcement-card";
import { HeroBannerCard } from "./components/hero-banner-card";
import { SocialLinksCard } from "./components/social-links-card";
import { useAnnouncementText } from "./hooks/use-announcement-text";
import { useSocialLinks } from "./hooks/use-social-links";

export function WebPageSettingsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation("settings");

  const announcement = useAnnouncementText();
  const socialLinks = useSocialLinks();
  // Subscribe via useFormState so React Compiler doesn't memoize away the
  // Proxy access on `form.formState.isDirty`.
  const { isDirty: announcementDirty } = useFormState({ control: announcement.form.control });
  const { isDirty: socialLinksDirty } = useFormState({ control: socialLinks.form.control });

  const isDirty = announcementDirty || socialLinksDirty;
  const saving = announcement.saving || socialLinks.saving;

  const save = async () => {
    const results = await Promise.allSettled([
      announcementDirty ? announcement.save() : undefined,
      socialLinksDirty ? socialLinks.save() : undefined,
    ]);
    // Each save toasts its own error; rethrow so the navigation guard stays put.
    const failure = results.find((result) => result.status === "rejected");
    if (failure) throw failure.reason;
  };

  // Each save already toasts its error; the header has nothing to recover.
  const saveFromHeader = () => save().catch(() => {});

  const discard = () => {
    announcement.discard();
    socialLinks.discard();
  };

  return (
    <NavigationGuardProvider
      hasUnsavedChanges={isDirty}
      onSave={save}
      onDiscard={discard}
      saving={saving}
      description={t("messages.unsavedChanges")}
    >
      <BaseNavigationBar
        title={t("sections.webPage.title")}
        subtitle={t("sections.webPage.description")}
        leadElement={
          isMobile ? <BackButton key="back-button" onBack={() => navigate(-1)} /> : undefined
        }
        trailingElements={[
          <SettingsSearchTrigger key="search-trigger" />,
          <FormActionButtons
            key="form-actions"
            isDirty={isDirty}
            onSave={saveFromHeader}
            onDiscard={discard}
            saving={saving}
          />,
        ]}
      />

      <PageContent>
        <PopoverErrorBoundary>
          <div className="space-y-6">
            <div id="field-web-hero-banner">
              <SettingCard
                title={t("storefront.heroBanner.title")}
                icon={<ImageIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />}
              >
                <HeroBannerCard />
              </SettingCard>
            </div>

            <div id="field-web-announcement">
              <AnnouncementCard
                form={announcement.form}
                loading={announcement.loading}
                saving={announcement.saving}
              />
            </div>

            <div id="field-web-social-links">
              <SettingCard
                title={t("storefront.socialLinks.title")}
                icon={<Share2 className="h-5 w-5 text-sky-600 dark:text-sky-400" />}
              >
                <SocialLinksCard
                  form={socialLinks.form}
                  loading={socialLinks.loading}
                  saving={socialLinks.saving}
                />
              </SettingCard>
            </div>
          </div>
        </PopoverErrorBoundary>
      </PageContent>
    </NavigationGuardProvider>
  );
}
